import {SR_ASSETS} from '@src/constants/asset-types';
import {ITEM_TYPES} from '@src/constants/item-types';
import {toAPIFilter} from '@src/utils/filters.js';
import {generatePSSceneSearchFilter} from '@src/utils/items.js';

const getSingleItemIdSearchFilter = ({id, selectedItemTypes}) => {
  // Handle search for single item ID
  const singleItemFilter = {
    type: 'StringInFilter',
    field_name: 'id',
    config: [id],
  };

  // Use the item type to allow for updating legacy item types when searching
  // by single item id. This will trigger a change to the filter in
  // worker-manager
  const itemTypeFilter = {
    type: 'StringInFilter',
    field_name: 'item_type',
    config: Object.keys(selectedItemTypes),
  };

  return {
    type: 'AndFilter',
    config: [singleItemFilter, itemTypeFilter],
  };
};

const getDateRangeFilters = (dateRanges, noEndDate, useNoEndDate) => {
  if (!dateRanges || dateRanges.length === 0) {
    return null;
  }

  const sortedDateRanges = [...dateRanges].sort((a, b) =>
    a > b ? 1 : b > a ? -1 : 0,
  );

  const config = sortedDateRanges.map((dates, index) => {
    const dateConfig = {
      type: 'DateRangeFilter',
      field_name: 'acquired',
      config: {
        gte: dates[0],
      },
    };

    const notOnLast =
      sortedDateRanges.length > 1 && index !== sortedDateRanges.length - 1;

    const ignoreEndDate = useNoEndDate && noEndDate !== false;

    if (notOnLast || !ignoreEndDate) {
      dateConfig.config.lte = dates[1];
    }

    return dateConfig;
  });

  return {
    type: 'OrFilter',
    config,
  };
};

const getItemTypeFilter = ({itemType, selectedImageryCapabilities}) => {
  // PSScene + asset filter handling
  if (itemType === ITEM_TYPES.PSSCENE) {
    return generatePSSceneSearchFilter(selectedImageryCapabilities);
  }

  return {
    type: 'StringInFilter',
    field_name: 'item_type',
    config: [itemType],
  };
};

const getInstrumentFilter = ({instruments, itemType}) => {
  if (instruments.length > 0 && itemType === ITEM_TYPES.PSSCENE) {
    return {
      type: 'StringInFilter',
      field_name: 'instrument',
      config: instruments,
    };
  }
};

const getPublishStageFilter = ({publishingStages, itemType}) => {
  if (publishingStages.length > 0 && itemType === ITEM_TYPES.PSSCENE) {
    return {
      type: 'StringInFilter',
      field_name: 'publishing_stage',
      config: publishingStages,
    };
  }
};

const getFiltersForItemType = ({
  itemType,
  instruments,
  metadataFilters,
  publishingStages,
  selectedImageryCapabilities,
}) => {
  const itemTypeFilter = getItemTypeFilter({
    itemType,
    selectedImageryCapabilities,
  });

  const instrumentFilter = getInstrumentFilter({
    instruments,
    itemType,
  });

  const publishStageFilter = getPublishStageFilter({
    itemType,
    publishingStages,
  });

  return {
    type: 'AndFilter',
    config: [
      itemTypeFilter,
      ...metadataFilters,
      instrumentFilter,
      publishStageFilter,
    ].filter(f => !!f),
  };
};

export const getSearchFilters = ({
  dateRanges,
  geometryTooLarge,
  noEndDate,
  searchFilters = {},
  selectedItemTypes,
  singleItemSearchId,
  updatedFilters,
  useNoEndDate = false,
}) => {
  const searchFiltersList = [];

  const getFilteredList = field =>
    Object.keys(searchFilters[field] ?? {}).filter(
      key => !!searchFilters[field][key],
    );

  const addFilter = filter => {
    if (filter) {
      searchFiltersList.push(filter);
    }
  };

  if (singleItemSearchId) {
    addFilter(
      getSingleItemIdSearchFilter({
        id: singleItemSearchId,
        selectedItemTypes,
      }),
    );
  } else {
    // Handle regular search
    if (!geometryTooLarge) {
      const instruments = getFilteredList('instruments');
      const publishingStages = getFilteredList('publishingStages');
      const metadataFilters = (updatedFilters ?? []).map(filter =>
        toAPIFilter(filter),
      );

      // Note: Running date filters first in query helps Data API ES backend
      // (not sure if this is still true)
      addFilter(getDateRangeFilters(dateRanges, noEndDate, useNoEndDate));

      addFilter({
        type: 'OrFilter',
        config: Object.keys(selectedItemTypes).map(itemType =>
          getFiltersForItemType({
            instruments,
            itemType,
            metadataFilters,
            publishingStages,
            selectedImageryCapabilities:
              searchFilters.selectedImageryCapabilities,
          }),
        ),
      });
    }

    if (searchFilters.sr) {
      addFilter({
        type: 'AssetFilter',
        config: SR_ASSETS,
      });
    }

    if (searchFilters.accessPermissions) {
      addFilter({
        type: 'PermissionFilter',
        config: ['assets:download', 'webtiles:stream'],
      });
    }

    if (searchFilters.satelliteId) {
      addFilter({
        type: 'StringInFilter',
        field_name: 'satellite_id',
        config: searchFilters.satelliteId.split(',').map(satId => satId.trim()),
      });
    }
  }

  return {
    type: 'AndFilter',
    config: searchFiltersList,
  };
};
